import { IApiClient } from "@common/network/interfaces";
import {
  ILoginPayloadData,
  IRegisterUserDataAfterOtpVerification,
  IResetPasswordParams,
  ISendOtpParams,
  IVerifyOtpParam,
} from "@common/redux/auth/interface";
import { getConfig } from "@config/index";

import {
  getUserPermissionDetails,
  userLogin,
  userLogout,
  userRefreshToken,
  userSignup,
  forgotPassword,
} from "../apis/urls";

const ENDPOINTS = {
  login: () => userLogin,
  logout: () => userLogout,
  refreshToken: () => userRefreshToken,
  registerUser: () => userSignup,
  sendOrValidateOtp: () => `api/otp/sendOrValidate`,
  resetPassword: () => forgotPassword,
  getUserPermissionDetails: () => getUserPermissionDetails,
};

export { ENDPOINTS as AuthEndPoints };

export interface IAuthRepository {
  login: (data: ILoginPayloadData) => Promise<any>;
  logout: () => Promise<any>;
  refreshToken: () => Promise<{
    authToken: string;
    tokenType: string;
  }>;
}

class AuthRepository implements IAuthRepository {
  private apiClient: IApiClient | null;
  constructor(apiClient: IApiClient | null) {
    this.apiClient = apiClient;
  }

  public resetApiClient(apiClient: IApiClient) {
    this.apiClient = apiClient;
  }
  public login = async (data: ILoginPayloadData) => {
    const result = await this.apiClient.post(ENDPOINTS.login(), data, {});
    const authToken = result.headers["authtoken"];
    const tokenType = result.headers["tokentype"];

    return {
      data: result.data,
      authToken: authToken,
      tokenType: tokenType,
    };
  };

  public logout = async () => {
    const result = await this.apiClient.post(ENDPOINTS.logout());
    return result;
  };

  public refreshToken = async () => {
    const result = await this.apiClient.post(ENDPOINTS.refreshToken());
    const authToken = result.headers["authtoken"];
    const tokenType = result.headers["tokentype"];
    const tokenExpiresIn = result.headers["expiresin"];
    return {
      authToken: authToken,
      tokenType: tokenType,
      tokenExpiresIn: tokenExpiresIn,
    };
  };

  public sendOtp = async (data: ISendOtpParams[]) => {
    const result = await this.apiClient.post(
      ENDPOINTS.sendOrValidateOtp(),
      data,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result;
  };

  public getUserPermissionData = async () => {
    const result = await this.apiClient.get(
      ENDPOINTS.getUserPermissionDetails(),
      "",
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public verifyOtp = async (data: IVerifyOtpParam[]) => {
    const result = await this.apiClient.post(
      ENDPOINTS.sendOrValidateOtp(),
      data,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result;
  };

  public registerUserAfterOtpVerification = async (
    data: IRegisterUserDataAfterOtpVerification
  ) => {
    const payload = data;
    const result = await this.apiClient.post(
      ENDPOINTS.registerUser(),
      payload,
      {}
    );
    const authToken = result.headers["authtoken"];
    const tokenType = result.headers["tokentype"];
    const tokenExpiresIn = result.headers["expiresin"];

    return {
      data: result.data,
      authToken: authToken,
      tokenType: tokenType,
      tokenExpiresIn: tokenExpiresIn,
    };
  };

  public resetPassword = async (data: IResetPasswordParams) => {
    const result = await this.apiClient.patch(
      ENDPOINTS.resetPassword(),
      data,
      {}
    );
    return result;
  };
}

const authRepository = new AuthRepository(null);

export { authRepository as AuthRepository };
